// ============================================================
// Tie-In — AdminTriage.jsx
// Admin panel: team triage status, stats, errors, questions.
// Owner/leadership only — role check enforced server-side.
// ============================================================

import { useState, useEffect } from 'react';
import { t } from './triageTheme.js';
import TierPill from './TierPill.jsx';
import { getAdminUsers, getAdminStats, getAdminErrors } from './triageApi.js';

// ── Shared primitives ─────────────────────────────────────────

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{
      display:      'flex',
      borderBottom: `1px solid ${t.colors.border}`,
      background:   t.colors.bgPanel,
    }}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          style={{
            flex:        1,
            padding:     '11px 4px',
            background:  'none',
            border:      'none',
            borderBottom: `2px solid ${active === tab.key ? t.colors.accent : 'transparent'}`,
            cursor:      'pointer',
            fontSize:    '12px',
            fontFamily:  t.fonts.body,
            fontWeight:  active === tab.key ? 500 : 400,
            color:       active === tab.key ? t.colors.textPrimary : t.colors.textSecondary,
            transition:  t.transitions.fast,
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div style={{
      background:   t.colors.bgPanel,
      border:       `1px solid ${highlight ? t.colors.accent + '55' : t.colors.border}`,
      borderRadius: t.radii.lg,
      padding:      '14px',
    }}>
      <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '26px', fontWeight: 500, fontFamily: t.fonts.mono,
        color: highlight ? t.colors.accent : t.colors.textPrimary }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body, marginTop: '4px' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function UserAvatar({ name }) {
  const initials = (name || '??').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  const colors   = [
    ['#E6F1FB','#185FA5'], ['#EEEDFE','#534AB7'], ['#EAF3DE','#3B6D11'],
    ['#FAEEDA','#854F0B'], ['#E1F5EE','#0F6E56'], ['#FCEBEB','#A32D2D'],
  ];
  const pair = colors[initials.charCodeAt(0) % colors.length];
  return (
    <div style={{
      width: '32px', height: '32px', borderRadius: '50%',
      background: pair[0], color: pair[1],
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '12px', fontWeight: 500, fontFamily: t.fonts.mono, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// ── Tab: Users ────────────────────────────────────────────────
function UsersTab({ users }) {
  if (!users.length) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: t.colors.textMuted, fontFamily: t.fonts.body, fontSize: '14px' }}>
      No users found
    </div>
  );

  return (
    <div style={{ padding: '12px' }}>
      {users.map(row => {
        const user    = row.users || {};
        const active  = row.triage_active && !row.oauth_invalid;
        const invalid = row.oauth_invalid;
        const paused  = !row.triage_active && !invalid;
        const pending = !row.onboarding_complete;

        const statusLabel = invalid  ? 'OAuth error'
          : pending  ? 'Not set up'
          : paused   ? 'Paused'
          :            'Active';
        const statusColor = invalid ? t.colors.danger
          : pending ? t.colors.warning
          : paused  ? t.colors.textMuted
          :           t.colors.success;

        return (
          <div key={row.user_id} style={{
            display:      'flex',
            alignItems:   'center',
            gap:          '10px',
            padding:      '11px 12px',
            background:   t.colors.bgPanel,
            border:       `1px solid ${invalid ? t.colors.danger + '44' : t.colors.border}`,
            borderRadius: t.radii.lg,
            marginBottom: '6px',
          }}>
            <UserAvatar name={user.name} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
                {user.name || 'Unknown user'}
              </div>
              <div style={{ fontSize: '11px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginTop: '2px' }}>
                {user.role?.replace(/_/g, ' ')}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', fontWeight: 500, color: statusColor, fontFamily: t.fonts.mono }}>
                {statusLabel}
              </div>
              {row.last_run_at && (
                <div style={{ fontSize: '10px', color: t.colors.textMuted, fontFamily: t.fonts.mono, marginTop: '2px' }}>
                  {new Date(row.last_run_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tab: Stats ────────────────────────────────────────────────
function StatsTab({ stats }) {
  if (!stats) return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: t.colors.textMuted, fontFamily: t.fonts.body, fontSize: '14px' }}>
      No stats available
    </div>
  );

  return (
    <div style={{ padding: '12px' }}>
      <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
        This week
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        <StatCard label="Emails triaged" value={stats.emails_found || 0} />
        <StatCard label="Drafts created" value={stats.drafts_created || 0} highlight />
        <StatCard label="Success rate"   value={`${stats.successRate || 0}%`} sub={`${stats.complete || 0} of ${stats.total || 0} runs`} />
        <StatCard label="LEAK alerts"    value={stats.leak_count || 0} />
      </div>

      <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
        Breakdown
      </div>
      <div style={{ background: t.colors.bgPanel, border: `1px solid ${t.colors.border}`, borderRadius: t.radii.lg, overflow: 'hidden' }}>
        {[
          { tier: 'LEAK',     count: stats.leak_count      || 0 },
          { tier: 'EOD',      count: stats.eod_count       || 0 },
          { tier: 'THIS_WEEK',count: stats.this_week_count || 0 },
          { tier: 'EXPENSE',  count: stats.expense_count   || 0 },
          { tier: 'LOW',      count: stats.low_count       || 0 },
        ].map((row, i) => (
          <div key={row.tier} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px',
            borderTop: i === 0 ? 'none' : `1px solid ${t.colors.border}`,
          }}>
            <TierPill tier={row.tier} />
            <span style={{ fontSize: '14px', fontFamily: t.fonts.mono, color: t.colors.textPrimary }}>
              {row.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Errors ───────────────────────────────────────────────
function ErrorsTab({ errors }) {
  if (!errors.length) return (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <div style={{ fontSize: '15px', fontWeight: 500, color: t.colors.success, fontFamily: t.fonts.body, marginBottom: '6px' }}>
        No errors
      </div>
      <div style={{ fontSize: '13px', color: t.colors.textSecondary, fontFamily: t.fonts.body }}>
        All runs completed successfully.
      </div>
    </div>
  );

  return (
    <div style={{ padding: '12px' }}>
      {errors.map(err => (
        <div key={err.id} style={{
          background:   t.colors.dangerDim,
          border:       `1px solid ${t.colors.danger}44`,
          borderRadius: t.radii.lg,
          padding:      '12px 14px',
          marginBottom: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
              {err.users?.name || err.user_id}
            </span>
            <span style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono }}>
              {new Date(err.run_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: t.colors.danger, fontFamily: t.fonts.mono }}>
            {err.error_message || 'Unknown error'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function AdminTriage() {
  const [tab,     setTab]     = useState('users');
  const [users,   setUsers]   = useState([]);
  const [stats,   setStats]   = useState(null);
  const [errors,  setErrors]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getAdminUsers().catch(() => []),
      getAdminStats().catch(() => null),
      getAdminErrors().catch(() => []),
    ]).then(([u, s, e]) => {
      setUsers(u);
      setStats(s);
      setErrors(e);
      setLoading(false);
    });
  }, []);

  const TABS = [
    { key: 'users',  label: 'Team' },
    { key: 'stats',  label: 'Stats' },
    { key: 'errors', label: `Errors${errors.length ? ` (${errors.length})` : ''}` },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '80px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: t.colors.textMuted, fontFamily: t.fonts.body, fontSize: '14px' }}>
            Loading...
          </div>
        ) : (
          <>
            {tab === 'users'  && <UsersTab  users={users}  />}
            {tab === 'stats'  && <StatsTab  stats={stats}  />}
            {tab === 'errors' && <ErrorsTab errors={errors} />}
          </>
        )}
      </div>
    </div>
  );
}
